import { useEffect, useMemo, useState } from 'react';
import type { MouseEvent } from 'react';
import { extractHeadings, outlineItems } from '@grc/markdown';

const STORAGE_KEY = 'za.corpus.outline';

function readStoredOpen(): boolean {
  try {
    return localStorage.getItem(STORAGE_KEY) !== '0';
  } catch {
    return true;
  }
}

function jumpTo(id: string) {
  const target = document.getElementById(id);
  if (!target) return;
  const reduced = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false;
  target.scrollIntoView({ behavior: reduced ? 'auto' : 'smooth', block: 'start' });
  // Headings are not focusable by default; focus so keyboard/AT users continue from the heading.
  target.tabIndex = -1;
  target.focus({ preventScroll: true });
}

/**
 * Does this document get an outline? Callers use it to decide the page layout —
 * the two-column grid must not be applied when no outline will render.
 */
export function hasOutline(markdown: string, minHeadings = 3): boolean {
  return outlineItems(extractHeadings(markdown), { minHeadings }).length > 0;
}

/**
 * Heading navigation for long markdown documents. Uses the same ids the shared
 * renderer emits, so the links work without JS; JS adds smooth scroll, focus
 * management and an active-heading highlight.
 */
export function DocumentOutline({ markdown, minHeadings = 3 }: { markdown: string; minHeadings?: number }) {
  const items = useMemo(() => outlineItems(extractHeadings(markdown), { minHeadings }), [markdown, minHeadings]);
  const [open, setOpen] = useState(readStoredOpen);
  const [activeId, setActiveId] = useState<string | null>(null);

  useEffect(() => {
    if (items.length === 0 || typeof IntersectionObserver === 'undefined') return;
    const elements = items
      .map((item) => document.getElementById(item.id))
      .filter((el): el is HTMLElement => el !== null);
    if (elements.length === 0) return;
    const visible = new Set<string>();
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) visible.add(entry.target.id);
          else visible.delete(entry.target.id);
        }
        // Highlight the first visible heading in document order.
        const first = elements.find((el) => visible.has(el.id));
        if (first) setActiveId(first.id);
      },
      { rootMargin: '0px 0px -60% 0px' },
    );
    for (const el of elements) observer.observe(el);
    return () => observer.disconnect();
  }, [items]);

  if (items.length === 0) return null;

  const toggle = () =>
    setOpen((current) => {
      try {
        localStorage.setItem(STORAGE_KEY, current ? '0' : '1');
      } catch {
        // Storage unavailable (private mode) — state still toggles for this view.
      }
      return !current;
    });

  const onLinkClick = (id: string) => (event: MouseEvent<HTMLAnchorElement>) => {
    event.preventDefault();
    history.replaceState(null, '', `#${id}`);
    jumpTo(id);
  };

  return (
    <nav aria-label="Contents" className={`doc-outline ${open ? '' : 'collapsed'}`}>
      <button
        type="button"
        className="doc-outline-toggle"
        aria-expanded={open}
        aria-controls="doc-outline-list"
        onClick={toggle}
      >
        <span className="doc-outline-title">Contents</span>
        <span className="doc-outline-chevron" aria-hidden="true">
          {open ? '−' : '+'}
        </span>
      </button>
      <ol id="doc-outline-list" className="doc-outline-list" hidden={!open}>
        {items.map((item) => (
          <li key={item.id} className={`doc-outline-item level-${item.level}`}>
            <a
              href={`#${item.id}`}
              className={`doc-outline-link ${activeId === item.id ? 'active' : ''}`}
              aria-current={activeId === item.id ? 'location' : undefined}
              onClick={onLinkClick(item.id)}
            >
              {item.text || 'Untitled section'}
            </a>
          </li>
        ))}
      </ol>
    </nav>
  );
}

/** "Back to top" link for the foot of long documents; shares the outline's threshold. */
export function BackToTop({ markdown, minHeadings = 3 }: { markdown: string; minHeadings?: number }) {
  const show = useMemo(() => hasOutline(markdown, minHeadings), [markdown, minHeadings]);
  if (!show) return null;
  return (
    <p className="doc-back-to-top">
      <a
        href="#top"
        onClick={(event) => {
          event.preventDefault();
          const reduced = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false;
          window.scrollTo({ top: 0, behavior: reduced ? 'auto' : 'smooth' });
          document.querySelector<HTMLElement>('.doc-outline-toggle')?.focus({ preventScroll: true });
        }}
      >
        Back to top
      </a>
    </p>
  );
}
